import {
  readProjectDesignSystemGenerationLineage,
} from '@/features/design-system/lib/project-design-system-generation-binding'
import { readStudioGenerationAuthority } from '@/features/generation-authority/generation-authority-readback'
import type { StudioProject } from '@/services/project-api'

/**
 * What a plan-rights cell can say, as a CODE rather than a sentence.
 *
 * This function used to return English display strings ("Not measured",
 * "Preview only", "Same-project publication admitted", …) which
 * `settings-page.tsx` rendered straight into a localized screen — measured
 * 2026-08-18 under `cogo-locale=ko`, "Not measured" was on screen three times.
 *
 * A code, not a message, because the read side then owns the language: the
 * dictionary declares `settings.plan.rights` as `Record<PlanRightsValue,
 * string>`, so adding an outcome here is a compile error in all five locale
 * tables instead of a fresh English string on a Korean surface.
 */
export type PlanRightsValue =
  | 'not-measured'
  | 'authority-readback-invalid'
  | 'not-admitted'
  | 'preview-only'
  | 'not-admitted-by-source-receipt'
  | 'preview-admitted'
  | 'same-project-publication-admitted'
  | 'admitted'

export interface PlanRightsViewModel {
  generation: PlanRightsValue
  publication: PlanRightsValue
  packPromotion: PlanRightsValue
  source: 'generationAuthority' | 'projectDesignSystemGeneration' | 'none'
}

export function buildPlanRightsViewModel(
  project: StudioProject | null,
): PlanRightsViewModel {
  if (!project) {
    return {
      generation: 'not-measured',
      publication: 'not-measured',
      packPromotion: 'not-measured',
      source: 'none',
    }
  }

  const authority = readStudioGenerationAuthority(project)
  if (authority.status === 'invalid') {
    return {
      generation: 'authority-readback-invalid',
      publication: 'not-admitted',
      packPromotion: 'not-admitted',
      source: 'generationAuthority',
    }
  }

  const lineage = readProjectDesignSystemGenerationLineage(project.settings)
  if (lineage) {
    return {
      generation: 'preview-only',
      publication: 'not-admitted-by-source-receipt',
      packPromotion: 'not-admitted',
      source: 'projectDesignSystemGeneration',
    }
  }

  if (authority.status !== 'available') {
    return {
      generation: 'not-measured',
      publication: 'not-admitted',
      packPromotion: 'not-admitted',
      source: 'none',
    }
  }

  return {
    generation: authority.envelope.rights.previewAllowed
      ? 'preview-admitted'
      : 'not-admitted',
    publication: authority.envelope.rights.sameProjectPublicationAllowed
      ? 'same-project-publication-admitted'
      : 'not-admitted',
    packPromotion: authority.envelope.rights.packPromotionAllowed
      ? 'admitted'
      : 'not-admitted',
    source: 'generationAuthority',
  }
}
